import { createAsyncThunk } from '@reduxjs/toolkit';

import { getErrorMessage } from '@/shared/utils/error';
import type { DocumentoListItem } from '@/types/processos/documentoStoreTypes';

import { documentosService } from './services';

export const listarDocumentos = createAsyncThunk<DocumentoListItem[], string>(
  'documentos/listar',
  async (execucaoId, { rejectWithValue }) => {
    try {
      return await documentosService.listar(execucaoId);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error)) as unknown as DocumentoListItem[];
    }
  },
);

export const uploadDocumento = createAsyncThunk<
  DocumentoListItem,
  {
    execucaoId: string;
    file: File;
    dados?: Partial<
      Pick<DocumentoListItem, 'tipoDocumento' | 'descricao' | 'sigilo' | 'ordemExibicao'>
    >;
  }
>('documentos/upload', async ({ execucaoId, file, dados }, { rejectWithValue }) => {
  try {
    return await documentosService.upload(execucaoId, file, dados);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error)) as unknown as DocumentoListItem;
  }
});

export const atualizarDocumento = createAsyncThunk<
  DocumentoListItem,
  { id: string; dados: Partial<DocumentoListItem> }
>('documentos/atualizar', async ({ id, dados }, { rejectWithValue }) => {
  try {
    return await documentosService.atualizar(id, dados);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error)) as unknown as DocumentoListItem;
  }
});

export const removerDocumento = createAsyncThunk<string, string>(
  'documentos/remover',
  async (id, { rejectWithValue }) => {
    try {
      await documentosService.remover(id);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error)) as unknown as string;
    }
  },
);

export const obterDownloadUrl = createAsyncThunk<string, string>(
  'documentos/downloadUrl',
  async (id, { rejectWithValue }) => {
    try {
      return await documentosService.downloadUrl(id);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error)) as unknown as string;
    }
  },
);

export const obterThumbnailUrl = createAsyncThunk<string, string>(
  'documentos/thumbnailUrl',
  async (id, { rejectWithValue }) => {
    try {
      return await documentosService.thumbnailUrl(id);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error)) as unknown as string;
    }
  },
);

export const substituirDocumento = createAsyncThunk<
  { message: string },
  { id: string; file: File }
>('documentos/substituir', async ({ id, file }, { rejectWithValue }) => {
  try {
    return await documentosService.substituir(id, file);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error)) as unknown as { message: string };
  }
});

export const visualizarDocumento = createAsyncThunk<void, string>(
  'documentos/visualizar',
  async (id, { rejectWithValue }) => {
    try {
      await documentosService.visualizar(id);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error)) as unknown as void;
    }
  },
);

export const baixarDocumento = createAsyncThunk<void, { id: string; nomeArquivo: string }>(
  'documentos/download',
  async ({ id, nomeArquivo }, { rejectWithValue }) => {
    try {
      await documentosService.download(id, nomeArquivo);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error)) as unknown as void;
    }
  },
);

export const uploadDocumentoParaProcesso = createAsyncThunk<
  void,
  {
    processoId: string;
    file: File;
    dados?: {
      tipoDocumento?: string;
      descricao?: string;
      sigilo?: boolean;
      interno?: boolean;
    };
  }
>('documentos/uploadParaProcesso', async ({ processoId, file, dados }, { rejectWithValue }) => {
  try {
    await documentosService.uploadParaProcesso(processoId, file, dados);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error)) as unknown as void;
  }
});
